import { loadWorkItemStore } from '@/stores/workItemStoreSlot';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { openInternalAddress } from './internalAddress';
import { loadInternalAddresses, loadedInternalAddressOf } from './internalAddressLoad';
import { originOf } from '@/features/work-items/fullPageOrigin';
import { useStores } from '@/hooks/useStores';
import { usePhoneNavigation } from '@/shell/usePhoneNavigation';
import { ownPageOf, panelItemOf, typeOfItem } from '@/features/work-areas/itemsOwnAddress';

/** The targets a plain click opens in place: none, this frame, and a new window (I-1357). */
const IN_PLACE_TARGETS = new Set(['', '_self', '_blank']);

/** The anchor a plain tap or click landed on, or `null` for a click that is not one to follow. */
function followedAnchor(event: MouseEvent): HTMLAnchorElement | null {
  if (event.defaultPrevented || event.button !== 0) return null;
  // A modified click is the reader asking for a tab or a window of their own.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  const target = event.target;
  if (!(target instanceof Element)) return null;
  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute('download')) return null;
  return anchor;
}

/**
 * I-1304: a tap on a link to a page of the app is the router's push, wherever the link is drawn.
 * Mounted once, under the router; renders nothing.
 *
 * I-1357 (IL-19g, IL-19h): one listener, on every surface, the Mac app included. An internal link
 * carries no `target`; one that still does (`_blank`, from an older drawer or pasted markup) opens in
 * place too, since a new tab is only ever the reader's own gesture. A modified or non-primary click is
 * never caught (`followedAnchor`), so ⌘, Ctrl, Shift and middle clicks stay the browser's (and the Mac
 * app's tab bar's). An anchor nobody handled is caught on the way up: left alone it loads the address
 * as a new document. A router link has prevented the default by then and is skipped, and a link to
 * the page the reader is on is left to scroll to its fragment.
 */
export function InternalLinkClicks(): null {
  const navigate = useNavigate();
  const location = useRef<{ pathname: string; search: string; hash: string }>(useLocation());
  location.current = useLocation();
  const phone = useRef(false);
  phone.current = usePhoneNavigation();
  const stores = useRef(useStores());
  stores.current = useStores();

  useEffect(() => {
    /*
      I-1357 (IL-19f): a link from a page of All work areas to an item's full page keeps All work
      areas, as `FullPageLink` does (I-0689's "remain in all work areas"): the push carries where the
      reader was, and Back pops to it.
    */
    const pushTo = (address: string): void => {
      const { projectStore } = stores.current;
      const holdsScope =
        !phone.current &&
        projectStore.allWorkAreas &&
        new URL(address, window.location.origin).searchParams.get('view') === 'full';
      openInternalAddress(navigate, address, {
        cameFrom: holdsScope ? originOf(location.current, { kind: 'all' }) : undefined,
      });
    };
    const open = (event: MouseEvent, anchor: HTMLAnchorElement): void => {
      const address = loadedInternalAddressOf(anchor.getAttribute('href'));
      if (!address) return;
      event.preventDefault();
      /*
        I-1304 (IL-15): an address that names the desktop's item panel is a page of All work areas
        with the item in its query, often a tab's root. Pushed as written on a phone, the root's
        `screen` reached the shell before the item's own page replaced it, and the shell moved the
        reader to that root's tab. The item's own page is found first, and it is the one push.
        I-1357 (IL-19e): on a desktop it is the item's full page.
      */
      const panel = panelItemOf(address);
      const { projectStore } = stores.current;
      const workArea = panel
        ? projectStore.projects.find((project) => project.id === panel.workAreaId)
        : undefined;
      if (!panel || !workArea) {
        pushTo(address);
        return;
      }
      // T-0770 (LC-6c): in the entry, so the item store is loaded here if no screen has yet.
      void loadWorkItemStore()
        .then((workItemStore) => typeOfItem(workItemStore, panel.workAreaId, panel.itemKey))
        .then((type) =>
          pushTo(ownPageOf(workArea, panel.itemKey, type, { fullPage: !phone.current })),
        );
    };
    const onAnchor = (event: MouseEvent) => {
      const anchor = followedAnchor(event);
      if (!anchor || !IN_PLACE_TARGETS.has(anchor.target)) return;
      const here = `${window.location.pathname}${window.location.search}`;
      const address = loadedInternalAddressOf(anchor.getAttribute('href'));
      // I-2453: the route table is not here yet (`internalAddressLoad.ts`): the click is the browser's.
      if (address === undefined) {
        void loadInternalAddresses().catch(() => undefined);
        return;
      }
      if (address === null || address.split('#', 1)[0] === here) return;
      open(event, anchor);
    };
    // I-2453: the route table loads when the browser is idle after the launch, or at the first press.
    const load = () => void loadInternalAddresses().catch(() => undefined);
    const idle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(load, { timeout: 3_000 })
        : window.setTimeout(load, 1_500);
    document.addEventListener('pointerdown', load, { capture: true, once: true });
    document.addEventListener('click', onAnchor);
    return () => {
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      document.removeEventListener('pointerdown', load, { capture: true });
      document.removeEventListener('click', onAnchor);
    };
  }, [navigate]);

  return null;
}
